import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const SalesTrendsChart = ({ salesTrends = [], isLoading = false }) => {
    if (isLoading) {
        return (
            <Paper sx={{ p: 3, height: 300 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Sales Trends (Last 7 Days)
                </Typography>
                <Box
                    sx={{
                        height: 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Loading...
                    </Typography>
                </Box>
            </Paper>
        );
    }

    const chartData = {
        labels: salesTrends.map((trend) => trend.day),
        datasets: [
            {
                label: "Orders",
                data: salesTrends.map((trend) => trend.orders),
                borderColor: "#ED2024",
                backgroundColor: "rgba(237, 32, 36, 0.1)",
                borderWidth: 3,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: "#ED2024",
                pointBorderColor: "#ffffff",
                pointBorderWidth: 2,
                pointRadius: 6,
                pointHoverRadius: 8,
            },
            {
                label: "Revenue (₹)",
                data: salesTrends.map((trend) => parseFloat(trend.revenue)),
                borderColor: "#4CAF50",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderWidth: 3,
                fill: false,
                tension: 0.4,
                pointBackgroundColor: "#4CAF50",
                pointBorderColor: "#ffffff",
                pointBorderWidth: 2,
                pointRadius: 6,
                pointHoverRadius: 8,
                yAxisID: "y1",
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top",
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12,
                        weight: "500",
                    },
                },
            },
            tooltip: {
                mode: "index",
                intersect: false,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleColor: "#ffffff",
                bodyColor: "#ffffff",
                borderColor: "#ED2024",
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        if (context.datasetIndex === 1) {
                            return `Revenue: ₹${context.parsed.y.toLocaleString()}`;
                        }
                        return `Orders: ${context.parsed.y}`;
                    },
                },
            },
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: "Days",
                    font: {
                        size: 12,
                        weight: "600",
                    },
                },
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                },
            },
            y: {
                type: "linear",
                display: true,
                position: "left",
                title: {
                    display: true,
                    text: "Orders",
                    font: {
                        size: 12,
                        weight: "600",
                    },
                },
                grid: {
                    color: "rgba(0, 0, 0, 0.1)",
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                },
            },
            y1: {
                type: "linear",
                display: true,
                position: "right",
                title: {
                    display: true,
                    text: "Revenue (₹)",
                    font: {
                        size: 12,
                        weight: "600",
                    },
                },
                grid: {
                    drawOnChartArea: false,
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                    callback: function (value) {
                        return `₹${value.toLocaleString()}`;
                    },
                },
            },
        },
        interaction: {
            mode: "nearest",
            axis: "x",
            intersect: false,
        },
    };

    return (
        <Paper sx={{ p: 3, height: 350 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Sales Trends (Last 7 Days)
            </Typography>
            <Box sx={{ height: 250, position: "relative" }}>
                <Line data={chartData} options={options} />
            </Box>
        </Paper>
    );
};

export default SalesTrendsChart;
